import React from 'react';
import { styled } from '@linaria/react';

import { dashToCamelCase, transformObject } from '@/utils';

import Demo, {
  DemoToggleButtonGroupControl,
} from '@/components/Demo';
import CodeSnippetProvider from '@/components/CodeSnippet/CodeSnippetProvider';

import { Svg } from '../BasicShapesDemo/BasicShapes.shared';
import {
  Variant,
  VARIANTS,
  INITIAL_VARIANT,
  generateSnippet,
} from './StrokeStylesDemo.helpers';

interface Props {
  codeOutput?: React.ReactNode;
}

const SVG_SIZE = 300;

function StrokeStylesDemo({ codeOutput }: Props) {
  return (
    <OuterDemoWrapper>
      <Demo
        outputWrapper={null}
        initialValues={{
          variant: INITIAL_VARIANT,
        }}
        controlPosition="top"
        controls={[
          <DemoToggleButtonGroupControl
            id="variant"
            key="variant"
            label="Variant"
            options={[
              { label: 'A', value: 'a' },
              { label: 'B', value: 'b' },
              { label: 'C', value: 'c' },
              { label: 'D', value: 'd' },
              { label: 'E', value: 'e' },
            ]}
          />,
        ]}
        customGrid={{
          gridTemplateColumns: 'min(400px, 100%)',
          justifyContent: 'center',
        }}
      >
        {({ variant }) => {
          const style = VARIANTS[variant as Variant];

          const camelCaseStyle = transformObject(
            style,
            (key, value) => [dashToCamelCase(key), value]
          );

          return (
            <Row>
              <StyledSvg
                viewBox="0 0 200 200"
                size={SVG_SIZE}
                data-variant={variant}
                fill="none"
              >
                <circle
                  cx="100"
                  cy="100"
                  r="50"
                  style={camelCaseStyle}
                />
              </StyledSvg>
              <CodeWrapper>
                <CodeSnippetProvider
                  lang="html"
                  code={generateSnippet(variant)}
                >
                  {codeOutput}
                </CodeSnippetProvider>
              </CodeWrapper>
            </Row>
          );
        }}
      </Demo>
    </OuterDemoWrapper>
  );
}

const BREAKPOINT = '47rem';

const OuterDemoWrapper = styled.div`
  @media (max-width: ${BREAKPOINT}) {
    width: 100%;
    max-width: 360px;
    margin-inline: auto;
  }
`;

const Row = styled.div`
  display: flex;
  gap: 16px;

  @media (max-width: ${BREAKPOINT}) {
    flex-direction: column;
    align-items: center;
  }
`;

const CodeWrapper = styled.div`
  flex: 1;
  --code-font-size: 0.875rem;
  --code-line-height: 1.4;
  --block-padding-override: 16px;
  --inline-padding-override: 16px;

  outline: 1px solid var(--color-gray-400);
  border-radius: 2px;

  @media (max-width: ${BREAKPOINT}) {
    width: 100%;
  }
`;

const StyledSvg = styled(Svg)`
  display: block;
  outline: 1px solid var(--color-text);
  border-radius: 2px;

  @media (prefers-reduced-motion: no-preference) {
    circle {
      /* prettier-ignore */
      --spring: linear(0, 0.003 0.4%, 0.012 0.9%, 0.026 1.4%, 0.049 2%, 0.109 3.3%, 0.311 7.2%, 0.409 9.3%, 0.463 10.6%, 0.513 11.9%, 0.562 13.3%, 0.606 14.7%, 0.651 16.3%, 0.691 17.9%, 0.729 19.6%, 0.762 21.3%, 0.792 23.1%, 0.82 25%, 0.846 27%, 0.868 29.1%, 0.889 31.3%, 0.907 33.6%, 0.923 36.1%, 0.937 38.7%, 0.949 41.5%, 0.959 44.5%, 0.968 47.8%, 0.976 51.3%, 0.987 59.1%, 0.994 68.8%, 0.998 81.4%, 1);

      transition:
        stroke 1200ms var(--spring),
        stroke-width 900ms var(--spring),
        stroke-dasharray 1500ms var(--spring),
        stroke-linecap 1000ms var(--spring);
    }
  }
`;

export default StrokeStylesDemo;
